import {
	ChatState,
	EventType,
	IChatEventChatMaximizedData,
	IChatEventChatMaximizedMessage,
	IChatEventChatMinimizedData,
	IChatEventChatMinimizedMessage,
	IChatEventMessage,
	isEventMaximizedChatMessage,
	isEventMinimizedChatMessage,
	MessageType,
	MinMaxSource,
} from '@enuan/chat__utils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { configurationContext } from '../configuration';
import { StoredKey } from '../../utils/enuanLocalStorage';
import { useSendMessage } from '../../hooks/core/messages.hooks';
import { IFrameState, IFrameStateCallback, frameStateContext } from './context';
import { ChannelState } from '../../interfaces/ChannelState';
import { useDidUpdateEffect } from '@avalenti89/react-ts-utils';
import { useLogError } from '../../components/ErrorBoundary/useLogError';
import { configContext } from '../config/context';
import { useEnuanLocalStorage } from '../../hooks/useEnuanLocalStorage';

export const useIsOpen = (
	_isOpen?: IFrameState['isOpen'],
	onMinimize?: (message: IChatEventChatMinimizedMessage) => void,
	onMaximize?: (message: IChatEventChatMaximizedMessage) => void,
	isStandalone?: IFrameState['isStandalone'],
): [IFrameState['isOpen'], IFrameStateCallback['toggleChat']] => {
	const { toggler_button, state } = useContext(configurationContext);
	const { channelState } = useContext(configContext);
	const { hidden } = toggler_button ?? {};
	const isActive = state !== ChatState.OFF && !!state;

	const logError = useLogError('useIsOpen');

	const { get, set } = useEnuanLocalStorage();

	const sendMessage = useSendMessage();

	const __isOpen = useMemo(() => {
		if (isStandalone) return { isOpen: true, source: MinMaxSource.SITE };

		const persistedIsOpen = _isOpen ??
			get(StoredKey.CHAT_IS_OPEN) ?? {
				isOpen: false,
			};

		if (!isActive && persistedIsOpen.isOpen)
			return {
				...persistedIsOpen,
				isOpen: false,
			};

		return persistedIsOpen;
	}, [_isOpen, isStandalone, get, isActive]);

	const [isOpen, setIsOpen] = useState<IFrameState['isOpen']>(__isOpen);

	useEffect(() => {
		if (!isStandalone && !isActive && isOpen.isOpen)
			setIsOpen((prev) => ({
				...prev,
				isOpen: false,
			}));
	}, [isActive, isOpen.isOpen, isStandalone]);

	useEffect(() => {
		if (channelState === ChannelState.CHANNEL_OPEN) {
			set(StoredKey.CHAT_IS_OPEN, isOpen, false);

			const { source = MinMaxSource.USER, payload } = isOpen;
			const _data: IChatEventChatMaximizedData | IChatEventChatMinimizedData = {
				name: isOpen.isOpen
					? EventType.CHAT_MAXIMIZED
					: EventType.CHAT_MINIMIZED,
				parameters: { source, payload },
			};

			const event: IChatEventMessage<
				IChatEventChatMaximizedData | IChatEventChatMinimizedData
			> = {
				_type: MessageType.EVENT,
				_data,
			};
			sendMessage(event, true)
				.then(() => {
					if (isEventMaximizedChatMessage(event)) onMaximize?.(event);
					else if (isEventMinimizedChatMessage(event)) onMinimize?.(event);
				})
				.catch((error) => logError('minmax', { error, payload: { event } }));
		}
	}, [
		isOpen,
		sendMessage,
		onMaximize,
		onMinimize,
		channelState,
		logError,
		set,
	]);

	const toggleChat = useCallback<IFrameStateCallback['toggleChat']>(
		(_isOpen, source = MinMaxSource.USER, payload) => {
			if (isStandalone) return;
			const targetIsOpen = _isOpen ?? !isOpen.isOpen;
			const newIsOpen = { isOpen: targetIsOpen, source, payload };
			const isExplicitClose = targetIsOpen === false;
			if (isExplicitClose || (!hidden && isActive)) setIsOpen(newIsOpen);
		},
		[hidden, isActive, isOpen, isStandalone],
	);

	return [isOpen, toggleChat];
};

export const usePage = (
	_page?: IFrameState['page'],
): {
	page: IFrameState['page'];
	togglePage: IFrameStateCallback['togglePage'];
	setPage: (page: IFrameState['page']) => void;
	formData: IFrameState['formData'];
} => {
	const { state, signup } = useContext(configurationContext);
	const { setLockInput } = useContext(configContext);
	const [page, setPage] = useState<IFrameState['page']>(_page);

	const [formData, setFormData] = useState<IFrameState['formData']>(
		signup
			? {
					config: signup,
				}
			: undefined,
	);

	useDidUpdateEffect(() => {
		setPage(_page);
	}, [_page]);

	const togglePage = useCallback<IFrameStateCallback['togglePage']>(
		(page, payload) => {
			if (state !== ChatState.OFF) {
				setPage(page);
				if (page === 'messages') setLockInput(false);
				if (page !== 'form')
					setFormData(signup ? { config: signup } : undefined);
				else setFormData(payload);
			}
		},
		[state, setLockInput, signup],
	);

	return { page, togglePage, setPage, formData };
};
